import React, { useState, useEffect } from 'react';
import { Database } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import Button from '@shared/ui/Button';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import EmptyState from '@shared/ui/EmptyState';
import { getCustomFieldValues } from '../../services/customFieldsApi';
import { CustomFieldValue } from '../../types/custom-fields';

interface CustomFieldViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityId: string;
  entityDisplayName: string;
  workspaceId?: string;
}

const CustomFieldViewModal: React.FC<CustomFieldViewModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityDisplayName,
  workspaceId
}) => {
  const [customFields, setCustomFields] = useState<CustomFieldValue[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (isOpen) {
      fetchCustomFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityType, entityId, workspaceId]);
  
  const fetchCustomFields = async () => {
    try {
      setLoading(true);
      const { values } = await getCustomFieldValues(entityType, entityId, workspaceId);
      setCustomFields(values);
    } catch (error) {
      setCustomFields([]);
    } finally {
      setLoading(false);
    }
  };
  
  const formatFieldValue = (value: any, fieldType: string): string => {
    if (value === null || value === undefined) {
      return 'Not set';
    }
    
    // The value is stored as { value: actualValue }
    const actualValue = value.value !== undefined ? value.value : value;
    
    switch (fieldType) {
      case 'boolean':
        return actualValue ? 'Yes' : 'No';
      case 'date':
        return new Date(actualValue).toLocaleDateString();
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: value.currency || 'USD'
        }).format(actualValue);
      case 'multi_select':
        return Array.isArray(actualValue) ? actualValue.join(', ') : String(actualValue);
      case 'richtext':
        // Return raw HTML for richtext fields (will be rendered with dangerouslySetInnerHTML)
        return actualValue || 'Not set';
      default:
        return String(actualValue);
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      title="Custom fields"
      description={entityDisplayName}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : customFields.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No custom field data yet"
          body="Organization admins can add custom field values."
        />
      ) : (
        <div className="space-y-4">
          {customFields.map(field => (
            <div
              key={field.id}
              className="border-b border-border-light pb-4 last:border-b-0 dark:border-border-dark"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    {field.field_name}
                  </div>
                  {field.field_description && (
                    <p className="mb-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      {field.field_description}
                    </p>
                  )}
                  {field.field_type === 'richtext' ? (
                    <div
                      className="prose prose-sm max-w-none text-base text-text-primary dark:prose-invert dark:text-text-dark-primary"
                      dangerouslySetInnerHTML={{
                        __html: formatFieldValue(field.value, field.field_type),
                      }}
                    />
                  ) : (
                    <div className="text-base text-text-primary dark:text-text-dark-primary">
                      {formatFieldValue(field.value, field.field_type)}
                    </div>
                  )}
                </div>
                {/* Only meaningful when viewing across workspaces — otherwise it repeats the page. */}
                {!workspaceId && field.workspace_name && (
                  <span className="ml-3 shrink-0 rounded bg-background-tertiary px-2 py-1 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                    {field.workspace_name}
                  </span>
                )}
              </div>
              <div className="mt-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Updated {new Date(field.updated_at).toLocaleDateString()}
                {field.created_by_name && ` by ${field.created_by_name}`}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default CustomFieldViewModal;
